'use client';

import { useState } from 'react';
import Link from 'next/link';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';

// ============================================================
// STATIC DATA — Replace with DB fetch later
// ============================================================
const mockAgreements = [
    {
        id: 'RA-001',
        ownerName: 'Rajesh Kumar',
        ownerPhone: '9876543210',
        ownerEmail: 'rajesh.kumar@gmail.com',
        ownerAddress: '12, Anna Nagar East, Chennai - 600102',
        tenantName: 'Priya Sharma',
        tenantPhone: '9123456789',
        tenantEmail: 'priya.sharma@gmail.com',
        tenantAddress: '45, T. Nagar, Chennai - 600017',
        tenantIdProofType: 'Aadhaar',
        tenantIdProofNumber: 'XXXX-XXXX-1234',
        propertyAddress: 'Flat 3B, Sunrise Apartments, OMR Road, Chennai - 600096',
        propertyType: 'Apartment',
        propertyArea: '1200',
        monthlyRent: '25000',
        securityDeposit: '150000',
        maintenanceCharges: '3000',
        agreementStartDate: '2024-06-01',
        agreementEndDate: '2025-05-31',
        lockInPeriod: '6',
        noticePeriod: '2',
        paymentDueDate: '5',
        termsAndConditions: 'Tenant shall not sublet the property. Owner is responsible for major repairs. Tenant must maintain cleanliness. No pets allowed without written consent.',
        status: 'Active',
        createdAt: '2024-05-28',
    },
    {
        id: 'RA-002',
        ownerName: 'Anita Patel',
        ownerPhone: '9988776655',
        ownerEmail: 'anita.patel@gmail.com',
        ownerAddress: '78, Besant Nagar, Chennai - 600090',
        tenantName: 'Vikram Singh',
        tenantPhone: '9876501234',
        tenantEmail: 'vikram.singh@gmail.com',
        tenantAddress: '23, Mylapore, Chennai - 600004',
        tenantIdProofType: 'PAN',
        tenantIdProofNumber: 'ABCDE1234F',
        propertyAddress: 'House No. 15, 2nd Cross Street, Adyar, Chennai - 600020',
        propertyType: 'Independent House',
        propertyArea: '2000',
        monthlyRent: '40000',
        securityDeposit: '200000',
        maintenanceCharges: '0',
        agreementStartDate: '2024-01-15',
        agreementEndDate: '2024-07-14',
        lockInPeriod: '3',
        noticePeriod: '1',
        paymentDueDate: '1',
        termsAndConditions: 'Tenant is responsible for utility bills. Painting to be done at the time of vacating. No structural changes allowed.',
        status: 'Expired',
        createdAt: '2024-01-10',
    },
    {
        id: 'RA-003',
        ownerName: 'Suresh Babu',
        ownerPhone: '9871234567',
        ownerEmail: 'suresh.babu@gmail.com',
        ownerAddress: '56, Velachery Main Road, Chennai - 600042',
        tenantName: 'Meena Devi',
        tenantPhone: '9001234567',
        tenantEmail: 'meena.devi@gmail.com',
        tenantAddress: '89, Guindy, Chennai - 600032',
        tenantIdProofType: 'Passport',
        tenantIdProofNumber: 'J1234567',
        propertyAddress: 'Shop No. 5, Ground Floor, KK Complex, Tambaram, Chennai - 600045',
        propertyType: 'Shop',
        propertyArea: '500',
        monthlyRent: '15000',
        securityDeposit: '90000',
        maintenanceCharges: '1500',
        agreementStartDate: '2025-01-01',
        agreementEndDate: '2025-12-31',
        lockInPeriod: '12',
        noticePeriod: '3',
        paymentDueDate: '10',
        termsAndConditions: 'Commercial use only. Tenant must obtain all necessary licenses. Operating hours: 8 AM to 10 PM.',
        status: 'Active',
        createdAt: '2024-12-20',
    },
    {
        id: 'RA-004',
        ownerName: 'Lakshmi Narayan',
        ownerPhone: '9845612378',
        ownerEmail: 'lakshmi.n@gmail.com',
        ownerAddress: '34, Porur, Chennai - 600116',
        tenantName: 'Karthik Rajan',
        tenantPhone: '9786543210',
        tenantEmail: 'karthik.r@gmail.com',
        tenantAddress: '67, Ashok Nagar, Chennai - 600083',
        tenantIdProofType: 'Driving License',
        tenantIdProofNumber: 'TN-01-2024-0012345',
        propertyAddress: 'Office No. 302, 3rd Floor, Tech Park, Sholinganallur, Chennai - 600119',
        propertyType: 'Office',
        propertyArea: '800',
        monthlyRent: '35000',
        securityDeposit: '210000',
        maintenanceCharges: '5000',
        agreementStartDate: '2025-03-01',
        agreementEndDate: '2026-02-28',
        lockInPeriod: '6',
        noticePeriod: '2',
        paymentDueDate: '7',
        termsAndConditions: 'Office use only. Parking for 2 cars included. Internet setup by tenant. Building amenities access included.',
        status: 'Pending',
        createdAt: '2025-02-15',
    },
];

const propertyTypes = ['Apartment', 'Independent House', 'Villa', 'Shop', 'Office', 'Warehouse', 'PG/Hostel'];
const idProofTypes = ['Aadhaar', 'PAN', 'Passport', 'Driving License', 'Voter ID'];
const statusOptions = ['Active', 'Expired', 'Pending', 'Terminated'];

const emptyForm = {
    ownerName: '', ownerPhone: '', ownerEmail: '', ownerAddress: '',
    tenantName: '', tenantPhone: '', tenantEmail: '', tenantAddress: '',
    tenantIdProofType: 'Aadhaar', tenantIdProofNumber: '',
    propertyAddress: '', propertyType: 'Apartment', propertyArea: '',
    monthlyRent: '', securityDeposit: '', maintenanceCharges: '',
    agreementStartDate: '', agreementEndDate: '',
    lockInPeriod: '', noticePeriod: '', paymentDueDate: '',
    termsAndConditions: '', status: 'Active',
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const formatCurrency = (amount) => {
    const num = Number(amount);
    if (!num) return '₹0';
    return `₹${num.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getStatusColor = (status) => {
    switch (status) {
        case 'Active': return 'bg-green-100 text-green-700 border-green-200';
        case 'Expired': return 'bg-red-100 text-red-700 border-red-200';
        case 'Pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        case 'Terminated': return 'bg-gray-100 text-gray-700 border-gray-200';
        default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
};

const getPropertyIcon = (type) => {
    switch (type) {
        case 'Apartment':
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />;
        case 'Independent House':
        case 'Villa':
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />;
        case 'Shop':
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />;
        case 'Office':
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.193 23.193 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />;
        default:
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />;
    }
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function RentalAgreementPage() {
    const [agreements, setAgreements] = useState(mockAgreements);
    const [filterStatus, setFilterStatus] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});

    // Filtering
    const filteredAgreements = agreements.filter(a => {
        const matchesStatus = filterStatus === 'All' || a.status === filterStatus;
        const matchesSearch = searchTerm === '' ||
            a.tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.id.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    // Counts
    const statusCounts = {
        All: agreements.length,
        Active: agreements.filter(a => a.status === 'Active').length,
        Expired: agreements.filter(a => a.status === 'Expired').length,
        Pending: agreements.filter(a => a.status === 'Pending').length,
        Terminated: agreements.filter(a => a.status === 'Terminated').length,
    };

    // Form handlers
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.ownerName.trim()) errors.ownerName = 'Required';
        if (!formData.ownerPhone.trim()) errors.ownerPhone = 'Required';
        if (!formData.ownerAddress.trim()) errors.ownerAddress = 'Required';
        if (!formData.tenantName.trim()) errors.tenantName = 'Required';
        if (!formData.tenantPhone.trim()) errors.tenantPhone = 'Required';
        if (!formData.tenantAddress.trim()) errors.tenantAddress = 'Required';
        if (!formData.tenantIdProofNumber.trim()) errors.tenantIdProofNumber = 'Required';
        if (!formData.propertyAddress.trim()) errors.propertyAddress = 'Required';
        if (!formData.propertyArea.trim()) errors.propertyArea = 'Required';
        if (!formData.monthlyRent.trim()) errors.monthlyRent = 'Required';
        if (!formData.securityDeposit.trim()) errors.securityDeposit = 'Required';
        if (!formData.agreementStartDate) errors.agreementStartDate = 'Required';
        if (!formData.agreementEndDate) errors.agreementEndDate = 'Required';
        return errors;
    };

    const handleAddNew = () => {
        setFormData(emptyForm);
        setFormErrors({});
        setIsEditMode(false);
        setIsAddModalOpen(true);
    };

    const handleCardClick = (agreement) => {
        setSelectedAgreement(agreement);
        setIsViewModalOpen(true);
        setIsEditMode(false);
    };

    const handleEditFromView = () => {
        setFormData({ ...selectedAgreement });
        setFormErrors({});
        setIsEditMode(true);
        setIsViewModalOpen(false);
        setIsAddModalOpen(true);
    };

    const handleFormSubmit = () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (isEditMode && selectedAgreement) {
            // Update existing
            setAgreements(prev => prev.map(a =>
                a.id === selectedAgreement.id ? { ...a, ...formData } : a
            ));
        } else {
            // Add new
            const newAgreement = {
                ...formData,
                id: `RA-${String(agreements.length + 1).padStart(3, '0')}`,
                createdAt: new Date().toISOString().split('T')[0],
            };
            setAgreements(prev => [newAgreement, ...prev]);
        }

        setIsAddModalOpen(false);
        setFormData(emptyForm);
        setFormErrors({});
        setSelectedAgreement(null);
        setIsEditMode(false);
    };

    const handlePrint = () => {
        window.print();
    };

    const closeAllModals = () => {
        setIsAddModalOpen(false);
        setIsViewModalOpen(false);
        setIsEditMode(false);
        setSelectedAgreement(null);
        setFormData(emptyForm);
        setFormErrors({});
    };

    // Input class helper
    const inputCls = (field) =>
        `w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${formErrors[field]
            ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-200'
            : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200'
        }`;

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray-50">

                {/* ─── Header ─── */}
                <section className="bg-white border-b border-gray-200 py-3">
                    <div className="container-custom py-6">
                        {/* Breadcrumb */}
                        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            <span className="text-gray-900 font-medium">Rental Agreements</span>
                        </nav>

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Rental Agreements</h1>
                                <p className="text-gray-500 mt-1">Manage all your rental agreements in one place</p>
                            </div>
                            <button
                                onClick={handleAddNew}
                                className="btn bg-green-600 hover:bg-green-700 text-white self-start flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add New Agreement
                            </button>
                        </div>
                    </div>
                </section>

                {/* ─── Filters & Search ─── */}
                <div className="container-custom py-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-6 mt-5">
                        {/* Status Filter Chips */}
                        <div className="flex gap-2 flex-wrap">
                            {['All', 'Active', 'Pending', 'Expired', 'Terminated'].map(s => (
                                <button
                                    key={s}
                                    onClick={() => setFilterStatus(s)}
                                    className={`px-4 py-1 rounded-full text-sm font-medium transition-colors border ${filterStatus === s
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                                        }`}
                                >
                                    {s} ({statusCounts[s] || 0})
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative md:ml-auto md:min-w-[300px]">
                            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search by name, property, ID..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors"
                            />
                        </div>
                    </div>

                    {/* ─── Agreement Cards Grid ─── */}
                    {filteredAgreements.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {filteredAgreements.map((agreement) => (
                                <div
                                    key={agreement.id}
                                    onClick={() => handleCardClick(agreement)}
                                    className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                                >
                                    {/* Card Header with Property Type Icon */}
                                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    {getPropertyIcon(agreement.propertyType)}
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-white/80 text-xs">{agreement.id}</p>
                                                <p className="text-white font-semibold text-sm">{agreement.propertyType}</p>
                                            </div>
                                        </div>
                                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${getStatusColor(agreement.status)}`}>
                                            {agreement.status}
                                        </span>
                                    </div>

                                    {/* Card Body */}
                                    <div className="px-5 py-4">
                                        {/* Rent Amount */}
                                        <div className="mb-3">
                                            <p className="text-2xl font-bold text-gray-900">{formatCurrency(agreement.monthlyRent)}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                                        </div>

                                        {/* Owner & Tenant */}
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                                <div className="text-sm overflow-hidden">
                                                    <span className="text-gray-500">Owner: </span>
                                                    <span className="font-medium text-gray-900 truncate">{agreement.ownerName}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                                <div className="text-sm overflow-hidden">
                                                    <span className="text-gray-500">Tenant: </span>
                                                    <span className="font-medium text-gray-900 truncate">{agreement.tenantName}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Property Address */}
                                        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                                            <svg className="w-3.5 h-3.5 inline mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            {agreement.propertyAddress}
                                        </p>

                                        {/* Duration */}
                                        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                                            <span>{formatDate(agreement.agreementStartDate)}</span>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                            <span>{formatDate(agreement.agreementEndDate)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-16 text-center bg-white rounded-xl border border-gray-200">
                            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-gray-500 text-lg font-medium">No agreements found</p>
                            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or add a new agreement</p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />

            {/* ═══════════════════════════════════════════════════
                VIEW DETAILS MODAL
            ═══════════════════════════════════════════════════ */}
            {isViewModalOpen && selectedAgreement && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => { setIsViewModalOpen(false); setSelectedAgreement(null); }}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col transform transition-all">

                            {/* Header */}
                            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 flex-shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
                                <div>
                                    <p className="text-blue-100 text-sm">{selectedAgreement.id}</p>
                                    <h2 className="text-xl font-bold text-white">Agreement Details</h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={handlePrint}
                                        className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2h2m4 0v2m0-2h.01M7 16h10" />
                                        </svg>
                                        Print
                                    </button>
                                    {/* Edit Button */}
                                    <button
                                        onClick={handleEditFromView}
                                        className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                                        title="Edit Agreement"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    {/* Close Button */}
                                    <button
                                        onClick={() => { setIsViewModalOpen(false); setSelectedAgreement(null); }}
                                        className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

                                {/* Status & Property Type */}
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${getStatusColor(selectedAgreement.status)}`}>
                                        {selectedAgreement.status}
                                    </span>
                                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                                        {selectedAgreement.propertyType} — {selectedAgreement.propertyArea} sq.ft
                                    </span>
                                    <span className="text-sm text-gray-500">Created: {formatDate(selectedAgreement.createdAt)}</span>
                                </div>

                                {/* Rent Summary */}
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
                                        <p className="text-xs text-green-600 font-medium mb-1">Monthly Rent</p>
                                        <p className="text-lg font-bold text-green-700">{formatCurrency(selectedAgreement.monthlyRent)}</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
                                        <p className="text-xs text-blue-600 font-medium mb-1">Security Deposit</p>
                                        <p className="text-lg font-bold text-blue-700">{formatCurrency(selectedAgreement.securityDeposit)}</p>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-100">
                                        <p className="text-xs text-purple-600 font-medium mb-1">Maintenance</p>
                                        <p className="text-lg font-bold text-purple-700">{formatCurrency(selectedAgreement.maintenanceCharges)}</p>
                                    </div>
                                </div>

                                {/* Owner Details */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        </div>
                                        Owner Details
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-900">{selectedAgreement.ownerName}</span></div>
                                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium text-gray-900">{selectedAgreement.ownerPhone}</span></div>
                                        <div><span className="text-gray-500">Email:</span> <span className="font-medium text-gray-900">{selectedAgreement.ownerEmail || '—'}</span></div>
                                        <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium text-gray-900">{selectedAgreement.ownerAddress}</span></div>
                                    </div>
                                </div>

                                {/* Tenant Details */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        </div>
                                        Tenant Details
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-900">{selectedAgreement.tenantName}</span></div>
                                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium text-gray-900">{selectedAgreement.tenantPhone}</span></div>
                                        <div><span className="text-gray-500">Email:</span> <span className="font-medium text-gray-900">{selectedAgreement.tenantEmail || '—'}</span></div>
                                        <div><span className="text-gray-500">ID Proof:</span> <span className="font-medium text-gray-900">{selectedAgreement.tenantIdProofType} — {selectedAgreement.tenantIdProofNumber}</span></div>
                                        <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium text-gray-900">{selectedAgreement.tenantAddress}</span></div>
                                    </div>
                                </div>

                                {/* Property Details */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        </div>
                                        Property Details
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                                        <div><span className="text-gray-500">Address:</span> <span className="font-medium text-gray-900">{selectedAgreement.propertyAddress}</span></div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><span className="text-gray-500">Type:</span> <span className="font-medium text-gray-900">{selectedAgreement.propertyType}</span></div>
                                            <div><span className="text-gray-500">Area:</span> <span className="font-medium text-gray-900">{selectedAgreement.propertyArea} sq.ft</span></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Agreement Terms */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        </div>
                                        Agreement Terms
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-gray-500">Start Date:</span> <span className="font-medium text-gray-900">{formatDate(selectedAgreement.agreementStartDate)}</span></div>
                                        <div><span className="text-gray-500">End Date:</span> <span className="font-medium text-gray-900">{formatDate(selectedAgreement.agreementEndDate)}</span></div>
                                        <div><span className="text-gray-500">Lock-in Period:</span> <span className="font-medium text-gray-900">{selectedAgreement.lockInPeriod || '—'} months</span></div>
                                        <div><span className="text-gray-500">Notice Period:</span> <span className="font-medium text-gray-900">{selectedAgreement.noticePeriod || '—'} months</span></div>
                                        <div><span className="text-gray-500">Payment Due:</span> <span className="font-medium text-gray-900">{selectedAgreement.paymentDueDate ? `${selectedAgreement.paymentDueDate}th of every month` : '—'}</span></div>
                                    </div>
                                </div>

                                {/* Terms & Conditions */}
                                {selectedAgreement.termsAndConditions && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Terms & Conditions</h3>
                                        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4 leading-relaxed">{selectedAgreement.termsAndConditions}</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                                <button
                                    onClick={() => { setIsViewModalOpen(false); setSelectedAgreement(null); }}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleEditFromView}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit Agreement
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════
                ADD / EDIT FORM MODAL
            ═══════════════════════════════════════════════════ */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeAllModals}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col transform transition-all">

                            {/* Header */}
                            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isEditMode ? 'bg-blue-100' : 'bg-green-100'}`}>
                                        {isEditMode ? (
                                            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                        ) : (
                                            <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        )}
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {isEditMode ? 'Edit Agreement' : 'New Rental Agreement'}
                                    </h2>
                                </div>
                                <button onClick={closeAllModals} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* Scrollable Body */}
                            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

                                {/* ── Owner Details ── */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        Owner Details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name <span className="text-red-500">*</span></label>
                                            <input type="text" name="ownerName" value={formData.ownerName} onChange={handleFormChange} placeholder="Enter owner name" className={inputCls('ownerName')} />
                                            {formErrors.ownerName && <p className="mt-1 text-xs text-red-600">{formErrors.ownerName}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Phone <span className="text-red-500">*</span></label>
                                            <input type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleFormChange} placeholder="Enter phone number" maxLength={10} className={inputCls('ownerPhone')} />
                                            {formErrors.ownerPhone && <p className="mt-1 text-xs text-red-600">{formErrors.ownerPhone}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Email</label>
                                            <input type="email" name="ownerEmail" value={formData.ownerEmail} onChange={handleFormChange} placeholder="Enter email" className={inputCls('ownerEmail')} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Address <span className="text-red-500">*</span></label>
                                            <textarea name="ownerAddress" value={formData.ownerAddress} onChange={handleFormChange} placeholder="Enter owner address" rows="2" className={inputCls('ownerAddress')}></textarea>
                                            {formErrors.ownerAddress && <p className="mt-1 text-xs text-red-600">{formErrors.ownerAddress}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Tenant Details ── */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        Tenant Details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name <span className="text-red-500">*</span></label>
                                            <input type="text" name="tenantName" value={formData.tenantName} onChange={handleFormChange} placeholder="Enter tenant name" className={inputCls('tenantName')} />
                                            {formErrors.tenantName && <p className="mt-1 text-xs text-red-600">{formErrors.tenantName}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Phone <span className="text-red-500">*</span></label>
                                            <input type="tel" name="tenantPhone" value={formData.tenantPhone} onChange={handleFormChange} placeholder="Enter phone number" maxLength={10} className={inputCls('tenantPhone')} />
                                            {formErrors.tenantPhone && <p className="mt-1 text-xs text-red-600">{formErrors.tenantPhone}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Email</label>
                                            <input type="email" name="tenantEmail" value={formData.tenantEmail} onChange={handleFormChange} placeholder="Enter email" className={inputCls('tenantEmail')} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">ID Proof Type</label>
                                            <select name="tenantIdProofType" value={formData.tenantIdProofType} onChange={handleFormChange} className={inputCls('tenantIdProofType')}>
                                                {idProofTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">ID Proof Number <span className="text-red-500">*</span></label>
                                            <input type="text" name="tenantIdProofNumber" value={formData.tenantIdProofNumber} onChange={handleFormChange} placeholder="Enter ID proof number" className={inputCls('tenantIdProofNumber')} />
                                            {formErrors.tenantIdProofNumber && <p className="mt-1 text-xs text-red-600">{formErrors.tenantIdProofNumber}</p>}
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Address <span className="text-red-500">*</span></label>
                                            <textarea name="tenantAddress" value={formData.tenantAddress} onChange={handleFormChange} placeholder="Enter tenant address" rows="2" className={inputCls('tenantAddress')}></textarea>
                                            {formErrors.tenantAddress && <p className="mt-1 text-xs text-red-600">{formErrors.tenantAddress}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Property Details ── */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        Property Details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Property Address <span className="text-red-500">*</span></label>
                                            <textarea name="propertyAddress" value={formData.propertyAddress} onChange={handleFormChange} placeholder="Enter property address" rows="2" className={inputCls('propertyAddress')}></textarea>
                                            {formErrors.propertyAddress && <p className="mt-1 text-xs text-red-600">{formErrors.propertyAddress}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                                            <select name="propertyType" value={formData.propertyType} onChange={handleFormChange} className={inputCls('propertyType')}>
                                                {propertyTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Area (sq.ft) <span className="text-red-500">*</span></label>
                                            <input type="number" name="propertyArea" value={formData.propertyArea} onChange={handleFormChange} placeholder="e.g. 1200" className={inputCls('propertyArea')} />
                                            {formErrors.propertyArea && <p className="mt-1 text-xs text-red-600">{formErrors.propertyArea}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Rent & Deposit ── */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                        Rent & Deposit
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Rent (₹) <span className="text-red-500">*</span></label>
                                            <input type="number" name="monthlyRent" value={formData.monthlyRent} onChange={handleFormChange} placeholder="e.g. 25000" className={inputCls('monthlyRent')} />
                                            {formErrors.monthlyRent && <p className="mt-1 text-xs text-red-600">{formErrors.monthlyRent}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Security Deposit (₹) <span className="text-red-500">*</span></label>
                                            <input type="number" name="securityDeposit" value={formData.securityDeposit} onChange={handleFormChange} placeholder="e.g. 150000" className={inputCls('securityDeposit')} />
                                            {formErrors.securityDeposit && <p className="mt-1 text-xs text-red-600">{formErrors.securityDeposit}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance (₹)</label>
                                            <input type="number" name="maintenanceCharges" value={formData.maintenanceCharges} onChange={handleFormChange} placeholder="e.g. 3000" className={inputCls('maintenanceCharges')} />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Agreement Terms ── */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                        Agreement Terms
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date <span className="text-red-500">*</span></label>
                                            <input type="date" name="agreementStartDate" value={formData.agreementStartDate} onChange={handleFormChange} className={inputCls('agreementStartDate')} />
                                            {formErrors.agreementStartDate && <p className="mt-1 text-xs text-red-600">{formErrors.agreementStartDate}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date <span className="text-red-500">*</span></label>
                                            <input type="date" name="agreementEndDate" value={formData.agreementEndDate} onChange={handleFormChange} className={inputCls('agreementEndDate')} />
                                            {formErrors.agreementEndDate && <p className="mt-1 text-xs text-red-600">{formErrors.agreementEndDate}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Lock-in Period (months)</label>
                                            <input type="number" name="lockInPeriod" value={formData.lockInPeriod} onChange={handleFormChange} placeholder="e.g. 6" className={inputCls('lockInPeriod')} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period (months)</label>
                                            <input type="number" name="noticePeriod" value={formData.noticePeriod} onChange={handleFormChange} placeholder="e.g. 2" className={inputCls('noticePeriod')} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Due Date (day of month)</label>
                                            <input type="number" name="paymentDueDate" value={formData.paymentDueDate} onChange={handleFormChange} placeholder="e.g. 5" min="1" max="31" className={inputCls('paymentDueDate')} />
                                        </div>
                                        {isEditMode && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                                <select name="status" value={formData.status} onChange={handleFormChange} className={inputCls('status')}>
                                                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                                        <textarea name="termsAndConditions" value={formData.termsAndConditions} onChange={handleFormChange} placeholder="Enter terms and conditions..." rows="3" className={inputCls('termsAndConditions')}></textarea>
                                    </div>
                                </div>

                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                                <button
                                    onClick={closeAllModals}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFormSubmit}
                                    className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${isEditMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {isEditMode ? 'Update Agreement' : 'Create Agreement'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════
                PRINTABLE AGREEMENT TEMPLATE (HIDDEN ON SCREEN)
            ═══════════════════════════════════════════════════ */}
            {selectedAgreement && (
                <div id="printable-agreement" className="hidden print:block font-serif text-gray-900 bg-white leading-relaxed text-justify p-0">
                    <style dangerouslySetInnerHTML={{
                        __html: `
                        @media print {
                            @page {
                                size: A4;
                                margin: 0;
                            }
                            body * {
                                visibility: hidden !important;
                            }
                            #printable-agreement, #printable-agreement * {
                                visibility: visible !important;
                            }
                            #printable-agreement {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                padding: 20mm;
                            }
                            .bond-header {
                                height: 90mm; /* Approx 3.5 inches for stamp paper */
                            }
                            .agreement-content {
                                font-size: 12pt;
                                line-height: 1.6;
                            }
                            .signature-box {
                                page-break-inside: avoid;
                            }
                        }
                    `}} />

                    <div className="bond-header"></div>

                    <div className="agreement-content">
                        <h1 className="text-center text-2xl font-bold uppercase underline mb-10">Rental Agreement</h1>

                        <p className="mb-6">
                            This Rental Agreement is made and executed on this <strong>{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong> at Chennai,
                        </p>

                        <div className="mb-8">
                            <p className="font-bold mb-2 uppercase text-sm tracking-widest border-b border-gray-200 pb-1">BY AND BETWEEN:</p>
                            <p className="mb-4">
                                <strong>{selectedAgreement.ownerName}</strong>, residing at {selectedAgreement.ownerAddress},
                                hereinafter referred to as the <strong>"LESSOR"</strong> (which expression shall mean and include his heirs, executors, administrators and assigns) of the ONE PART.
                            </p>
                            <p className="text-center font-bold mb-4 italic">AND</p>
                            <p>
                                <strong>{selectedAgreement.tenantName}</strong>, residing at {selectedAgreement.tenantAddress},
                                hereinafter referred to as the <strong>"LESSEE"</strong> (which expression shall mean and include his heirs, executors, administrators and assigns) of the OTHER PART.
                            </p>
                        </div>

                        <p className="mb-6">
                            <strong>WHEREAS</strong> the Lessor is the absolute owner of the property situated at <strong>{selectedAgreement.propertyAddress}</strong>,
                            hereinafter referred to as the "SCHEDULED PREMISES".
                        </p>

                        <p className="mb-6 italic underline font-semibold">NOW THIS AGREEMENT WITNESSETH AS FOLLOWS:</p>

                        <ol className="list-decimal ml-8 space-y-5">
                            <li>
                                <strong>RENT:</strong> The monthly rent for the scheduled premises is fixed at <strong>{formatCurrency(selectedAgreement.monthlyRent)}</strong>.
                                The rent shall be paid on or before the <strong>{selectedAgreement.paymentDueDate || '5'}th</strong> day of every English calendar month.
                            </li>
                            <li>
                                <strong>DEPOSIT:</strong> The Lessee has paid a security deposit of <strong>{formatCurrency(selectedAgreement.securityDeposit)}</strong> to the Lessor.
                                This amount is interest-free and shall be refunded to the Lessee at the time of vacating the premises after deducting any dues or damages.
                            </li>
                            <li>
                                <strong>DURATION:</strong> The agreement shall be for a period of 11 months commencing from <strong>{formatDate(selectedAgreement.agreementStartDate)}</strong> to <strong>{formatDate(selectedAgreement.agreementEndDate)}</strong>.
                            </li>
                            <li>
                                <strong>MAINTENANCE:</strong> The monthly maintenance charges of <strong>{formatCurrency(selectedAgreement.maintenanceCharges || '0')}</strong> shall be paid by the Lessee.
                            </li>
                            <li>
                                <strong>LOCK-IN PERIOD:</strong> There shall be a lock-in period of <strong>{selectedAgreement.lockInPeriod || '6'} months</strong> during which neither party can terminate the agreement.
                            </li>
                            <li>
                                <strong>NOTICE PERIOD:</strong> After the lock-in period, either party can terminate the agreement by giving <strong>{selectedAgreement.noticePeriod || '1'} month(s)</strong> prior notice.
                            </li>
                            {selectedAgreement.termsAndConditions && (
                                <li>
                                    <strong>ADDITIONAL TERMS:</strong> {selectedAgreement.termsAndConditions}
                                </li>
                            )}
                        </ol>

                        <div className="mt-24 grid grid-cols-2 gap-20 signature-box">
                            <div className="text-center">
                                <div className="border-t border-black pt-2 font-bold">LESSOR (OWNER)</div>
                            </div>
                            <div className="text-center">
                                <div className="border-t border-black pt-2 font-bold">LESSEE (TENANT)</div>
                            </div>
                        </div>

                        <div className="mt-24 signature-box">
                            <p className="font-bold mb-10">WITNESSES:</p>
                            <div className="space-y-12">
                                <div>1. ____________________________</div>
                                <div>2. ____________________________</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
